import { Component, Input, Output, EventEmitter } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { BackgroundOperationViewModel } from 'src/app/background-operations/models/view-models/background-operation.view-model';
import { BackgroundOperationState } from 'src/app/background-operations/models/enums/background-operation-state.enum';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { LoggerService } from 'src/app/telemetry/logger.service';

/** Component for displaying a list of background operations. */
@Component({
	selector: 'crownx-background-operations-list',
	standalone: true,
	imports: [
		NgIf,
		NgFor,
		MatListModule,
		MatIconModule
	],
	templateUrl: './background-operations-list.component.html',
	styleUrls: ['./background-operations-list.component.css']
})
export class BackgroundOperationsListComponent {
	/** List of background operations to display */
	@Input()
	public BackgroundOperations: BackgroundOperationViewModel[] = [];

	/** Event emitted when a background operation is clicked */
	@Output()
	public OperationClicked = new EventEmitter<BackgroundOperationViewModel>();

	/**
	 * Indicates whether the "no operations" message should be displayed.
	 * @returns true if the BackgroundOperations list is empty; otherwise, false.
	 * @readonly
	 */
	public get ShouldNoOperationsMessageDisplay(): boolean {
		return this.BackgroundOperations.length === 0;
	}

	/**
	 * @constructor
	 * 
	 * @param logger The logging service.
	 */
	constructor(
		private readonly logger: LoggerService
	) { }

	/**
	 * Gets the CSS class for the status text/icon.
	 * @param backgroundOperation The background operation.
	 * @param isIcon Whether the class is for an icon.
	 * @returns The CSS class for the status.
	 */
	public GetStatusClass(backgroundOperation: BackgroundOperationViewModel, isIcon: boolean = false): string {
		if (backgroundOperation.IsProcessing) {
			return isIcon ? 'status-processing spin' : 'status-processing';
		}

		switch (backgroundOperation.Status) {
			case BackgroundOperationState.Success:
				return 'status-success';
			case BackgroundOperationState.Failed:
				return 'status-failed';
			case BackgroundOperationState.Canceled:
				return 'status-canceled';
			default:
				this.TrackInvalidStatusError(backgroundOperation.Status);
				return '';
		}
	}

	/**
	 * Gets the icon name for the given status.
	 * @param backgroundOperation The background operation.
	 * @returns The icon name for the status.
	 */
	public GetIcon (backgroundOperation: BackgroundOperationViewModel): string {
		if (backgroundOperation.IsProcessing) {
			return 'cached';
		}

		switch (backgroundOperation.Status) {
			case BackgroundOperationState.Success:
				return 'check_circle';
			case BackgroundOperationState.Failed:
				return 'error';
			case BackgroundOperationState.Canceled:
				return 'cancel';
			default:
				this.TrackInvalidStatusError(backgroundOperation.Status);
				return '';
		}
	}

	/**
	 * Gets the display name for the given status.
	 * @param backgroundOperation The background operation.
	 * @returns The display name for the status.
	 */
	public GetStatusDisplayName(backgroundOperation: BackgroundOperationViewModel): string {
		if (backgroundOperation.IsProcessing) {
			return 'Processing';
		}

		switch (backgroundOperation.Status) {
			case BackgroundOperationState.Success:
				return 'Completed';
			case BackgroundOperationState.Failed:
				return 'Failed';
			case BackgroundOperationState.Canceled:
				return 'Canceled';
			default:
				this.TrackInvalidStatusError(backgroundOperation.Status);
				return 'Unknown';
		}
	}

	/**
	 * Handles the click event on a background operation.
	 * @param operation The background operation that was clicked.
	 */
	public OnOperationClick(operation: BackgroundOperationViewModel): void {
		this.OperationClicked.emit(operation);
	}

	/**
	 * Tracks an error for an invalid background operation status.
	 * @param status The invalid background operation status.
	 */
	private TrackInvalidStatusError(status: BackgroundOperationState): void {
		const errorMessage: string = `Invalid value for BackgroundOperationState: ${status}`
		this.logger.TrackError(errorMessage, new Error(errorMessage));
	}
}
